import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { scrollToSection } from "@/lib/lenis";

const SHOTS = {
  home: "https://customer-assets-m6fa6gv7.emergentagent.net/job_rosteno-welcome/artifacts/hzyrbx9a_IMG_1224.webp",
  week: "https://customer-assets-m6fa6gv7.emergentagent.net/job_rosteno-welcome/artifacts/rwbeq6yx_IMG_1225.webp",
  day: "https://customer-assets-m6fa6gv7.emergentagent.net/job_rosteno-welcome/artifacts/x0mcesa2_IMG_1226.webp",
};

const LINES = [
  { text: "WORK CHANGES.", italic: false },
  { text: "LIFE", italic: false },
  { text: "SHOULDN'T.", italic: true },
];

const lineAnim = (i) => ({
  initial: { y: "110%" },
  animate: { y: "0%" },
  transition: { duration: 1.2, delay: 0.35 + i * 0.14, ease: [0.16, 1, 0.3, 1] },
});

function Phone({ src, alt, testId, className }) {
  return (
    <div
      className={`rounded-[2rem] border border-white/15 overflow-hidden bg-[#0b1526] shadow-[0_40px_80px_-20px_rgba(0,0,0,0.8)] ${className}`}
    >
      <img
        data-testid={testId}
        src={src}
        alt={alt}
        className="w-full h-full object-cover object-top"
      />
    </div>
  );
}

export default function Hero() {
  const fanRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: fanRef,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);

  return (
    <section
      data-testid="hero-section"
      className="relative pt-40 md:pt-52 pb-24 px-6 md:px-12 text-center overflow-hidden"
    >
      <motion.p
        data-testid="hero-eyebrow"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 0.25 }}
        className="font-mono-accent text-xs sm:text-sm uppercase tracking-[0.3em] text-[#8B99AE] mb-10"
      >
        Rosteno — For people who don't work 9–5
      </motion.p>

      <h1
        data-testid="hero-heading"
        className="font-serif-display font-medium text-5xl sm:text-6xl md:text-7xl lg:text-8xl tracking-tighter leading-[0.95]"
      >
        {LINES.map((line, i) => (
          <span key={line.text} className="block overflow-hidden pb-1">
            <motion.span
              data-testid={`hero-heading-line-${i + 1}`}
              className={`block ${line.italic ? "text-[#4ADE80]" : ""}`}
              {...lineAnim(i)}
            >
              {line.text}
            </motion.span>
          </span>
        ))}
      </h1>

      <motion.p
        data-testid="hero-subcopy"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 1.1, ease: [0.16, 1, 0.3, 1] }}
        className="max-w-xl mx-auto mt-10 text-sm sm:text-base leading-relaxed text-[#8B99AE]"
      >
        The go-to app for shift workers. ROSTENO turns your changing roster
        into a personal plan for sleep, training, nutrition, recovery and family
        — then rebuilds it the moment the roster changes.
      </motion.p>

      <motion.div
        data-testid="hero-launch-badges"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 1.3, ease: [0.16, 1, 0.3, 1] }}
        className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4"
      >
        <button
          data-testid="hero-appstore-badge"
          onClick={() => scrollToSection("#waitlist")}
          className="group flex items-center gap-3 border border-white/25 rounded-2xl px-6 py-3 transition-colors duration-300 hover:bg-white hover:text-black"
        >
          <svg viewBox="0 0 384 512" className="w-5 h-5 fill-current" aria-hidden="true">
            <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
          </svg>
          <span className="text-left leading-tight">
            <span className="block font-mono-accent text-[10px] uppercase tracking-[0.2em] opacity-60">
              Soon on the
            </span>
            <span className="block text-sm font-semibold">App Store</span>
          </span>
        </button>
        <button
          data-testid="hero-googleplay-badge"
          onClick={() => scrollToSection("#waitlist")}
          className="group flex items-center gap-3 border border-white/25 rounded-2xl px-6 py-3 transition-colors duration-300 hover:bg-white hover:text-black"
        >
          <svg viewBox="0 0 512 512" className="w-5 h-5 fill-current" aria-hidden="true">
            <path d="M325.3 234.3L104.6 13l280.8 161.2-60.1 60.1zM47 0C34 6.8 25.3 19.2 25.3 35.3v441.3c0 16.1 8.7 28.5 21.7 35.3l256.6-256L47 0zm425.2 225.6l-58.9-34.1-65.7 64.5 65.7 64.5 60.1-34.1c18-14.3 18-46.5-1.2-60.8zM104.6 499l280.8-161.2-60.1-60.1L104.6 499z" />
          </svg>
          <span className="text-left leading-tight">
            <span className="block font-mono-accent text-[10px] uppercase tracking-[0.2em] opacity-60">
              Soon on
            </span>
            <span className="block text-sm font-semibold">Google Play</span>
          </span>
        </button>
      </motion.div>

      <motion.div
        ref={fanRef}
        data-testid="hero-phone-frame"
        initial={{ opacity: 0, y: 80 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.3, delay: 1.25, ease: [0.16, 1, 0.3, 1] }}
        className="max-w-5xl mx-auto mt-20 md:mt-28"
      >
        <motion.div style={{ y }} className="flex items-end justify-center">
          <Phone
            src={SHOTS.week}
            alt="ROSTENO My Week screen showing the roster and plan"
            testId="hero-phone-left"
            className="hidden md:block w-[230px] lg:w-[270px] aspect-[9/19] -mr-14 lg:-mr-20 -rotate-[7deg] translate-y-10"
          />
          <Phone
            src={SHOTS.home}
            alt="ROSTENO Home screen with today's shift, sleep target, training and meals"
            testId="hero-phone-image"
            className="w-[260px] sm:w-[300px] lg:w-[340px] aspect-[9/19] z-10"
          />
          <Phone
            src={SHOTS.day}
            alt="ROSTENO My Day screen with the planned day timeline"
            testId="hero-phone-right"
            className="hidden md:block w-[230px] lg:w-[270px] aspect-[9/19] -ml-14 lg:-ml-20 rotate-[7deg] translate-y-10"
          />
        </motion.div>
        <p
          data-testid="hero-phone-caption"
          className="mt-12 font-mono-accent text-xs uppercase tracking-[0.25em] text-[#8B99AE]"
        >
          The roster is the input. A better life is the output.
        </p>
      </motion.div>
    </section>
  );
}
